'use client';

import { Plus, Settings, X } from 'lucide-react';

import CollectionDropdown from '@/components/collection-dropdown';
import type { TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import type { ReaderTone } from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';
import { ChaptersDropdown } from '@/features/reader/components/chapters-dropdown';
import { cn } from '@/lib/utils';
import {
  toolbarActionClassName,
  toolbarActionsClassName,
  toolbarAddTriggerClassName,
  toolbarCollectionPopoverClassName,
  toolbarDividerClassName,
  toolbarIconButtonClassName,
  toolbarSlotClassName,
  toolbarSurfaceClassName,
} from './reader-toolbar.style';

interface ReaderToolbarProps {
  tone: ReaderTone;
  visible: boolean;
  chapters: TChapterWithoutContent[];
  selectedChapterId: string;
  onChapterChange: (chapterId: string) => void;
  onChaptersOpenChange: (open: boolean) => void;
  workId?: string;
  workPathPrefix?: '/fanfics' | '/originals';
  isRating18?: boolean;
  showAddAction: boolean;
  fanficId: string;
  collectionStatus?: string;
  isInCollection: boolean;
  chaptersCount: number;
  onCollectionPresenceChange: (active: boolean) => void;
  onCollectionStatusChange: (status: string, isPrivate?: boolean) => void;
  onAddToCollection: (status: string, isPrivate?: boolean) => void;
  onRemoveFromCollection: () => void;
  settingsButtonRef: React.RefObject<HTMLButtonElement | null>;
  settingsOpen: boolean;
  onSettingsToggle: () => void;
  onClose: () => void;
  onPinnedChange: (pinned: boolean) => void;
}

export function ReaderToolbar({
  tone,
  visible,
  chapters,
  selectedChapterId,
  onChapterChange,
  onChaptersOpenChange,
  workId,
  workPathPrefix,
  isRating18,
  showAddAction,
  fanficId,
  collectionStatus,
  isInCollection,
  chaptersCount,
  onCollectionPresenceChange,
  onCollectionStatusChange,
  onAddToCollection,
  onRemoveFromCollection,
  settingsButtonRef,
  settingsOpen,
  onSettingsToggle,
  onClose,
  onPinnedChange,
}: ReaderToolbarProps) {
  return (
    <div className={toolbarSlotClassName}>
      <div
        data-reader-chrome
        data-reader-toolbar
        data-tone={tone}
        className={cn(toolbarSurfaceClassName, !visible && 'pointer-events-none')}
        style={{
          transform: visible ? 'translate3d(0, 0, 0)' : 'translate3d(0, calc(-100% - 16px), 0)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
        inert={visible ? undefined : true}
        aria-hidden={visible ? undefined : true}
        onMouseEnter={() => onPinnedChange(true)}
        onMouseLeave={() => onPinnedChange(false)}
        onFocus={() => onPinnedChange(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            onPinnedChange(false);
          }
        }}
      >
        <ChaptersDropdown
          chapters={chapters}
          selectedChapterId={selectedChapterId}
          setSelectedChapterId={onChapterChange}
          workId={workId}
          workPathPrefix={workPathPrefix}
          isRating18={isRating18}
          tone={tone}
          onOpenChange={onChaptersOpenChange}
        />

        <div className={toolbarActionsClassName}>
          {showAddAction && (
            <CollectionDropdown
              postId={fanficId}
              currentStatus={collectionStatus}
              onStatusChange={onCollectionStatusChange}
              onAddToCollection={onAddToCollection}
              onRemoveFromCollection={onRemoveFromCollection}
              isInCollection={isInCollection}
              chaptersCount={chaptersCount}
              tone={tone}
              className={toolbarActionClassName}
              popoverContentClassName={toolbarCollectionPopoverClassName}
              onPresenceChange={onCollectionPresenceChange}
              trigger={
                <span className={toolbarAddTriggerClassName}>
                  <Plus className="size-4" aria-hidden="true" />
                  <span className="max-sm:hidden">Добавить</span>
                </span>
              }
            />
          )}
          {showAddAction && <span className={toolbarDividerClassName} aria-hidden="true" />}
          <button
            ref={settingsButtonRef}
            type="button"
            className={toolbarIconButtonClassName}
            aria-label="Настройки чтения"
            aria-expanded={settingsOpen}
            onClick={onSettingsToggle}
          >
            <Settings className="size-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={toolbarIconButtonClassName}
            aria-label="Закрыть"
            onClick={onClose}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
